import { Controller } from "@hotwired/stimulus"

// Lightweight in-page tabs. Tab buttons carry data-tabs-target="tab" +
// data-tab="<name>"; content panels carry data-tabs-target="panel" +
// data-tab="<name>". The active tab is data-tabs-active-value (defaults to
// the first tab), and switching just toggles which panel shows — no
// round-trips.
//
// A heavy panel can defer its cost by wrapping its content in an inert
// <template data-tabs-lazy>: the browser doesn't render it or fetch its
// images until we clone it in on first activation (e.g. a 200-avatar people
// grid stays cheap until the People tab is opened).
export default class extends Controller {
    static targets = ["tab", "panel"]
    static values = { active: String, history: Boolean }

    connect() {
        // A link to a section (#ask-me) opens the tab it lives in.
        const anchored = this._anchoredPanel()
        this.show(anchored?.dataset.tab || this._tabForCurrentPath() || this.activeValue || this.tabTargets[0]?.dataset.tab)
        if (anchored) requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" }))
        if (this.hasHistoryValue && this.historyValue) {
            this._onPopState = () => {
                const tab = this._tabForCurrentPath()
                if (!tab) return
                this.show(tab)
                this.element.dispatchEvent(new CustomEvent("profile-tabs:history", { bubbles: true, detail: { tab } }))
            }
            window.addEventListener("popstate", this._onPopState)
        }
    }

    disconnect() {
        if (this._onPopState) window.removeEventListener("popstate", this._onPopState)
    }

    _anchoredPanel() {
        const id = location.hash.slice(1)
        if (!id) return null
        const target = document.getElementById(decodeURIComponent(id))
        return target && this.element.contains(target) ? this.panelTargets.find((panel) => panel.contains(target)) : null
    }

    select(event) {
        event.preventDefault()
        const tab = event.currentTarget.dataset.tab
        this.show(tab)
        if (this.hasHistoryValue && this.historyValue && event.currentTarget.href) {
            const url = new URL(event.currentTarget.href, window.location.href)
            if (url.pathname !== window.location.pathname) window.history.pushState({}, "", url)
        }
    }

    _tabForCurrentPath() {
        if (!this.hasHistoryValue || !this.historyValue) return null
        return this.tabTargets.find((tab) => tab.href && new URL(tab.href, window.location.href).pathname === window.location.pathname)?.dataset.tab
    }

    show(name) {
        if (!name) return
        this.tabTargets.forEach((tab) => {
            const active = tab.dataset.tab === name
            tab.classList.toggle("is-active", active)
            tab.setAttribute("aria-selected", active ? "true" : "false")
        })
        this.panelTargets.forEach((panel) => {
            const active = panel.dataset.tab === name
            panel.hidden = !active
            if (active) this._hydrate(panel)
        })
    }

    // Clone an inert <template data-tabs-lazy> into its panel the first time
    // the panel is shown, then drop the template so it only happens once.
    _hydrate(panel) {
        const template = panel.querySelector("template[data-tabs-lazy]")
        if (!template) return
        panel.appendChild(template.content.cloneNode(true))
        template.remove()
    }
}
